import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, Divider, useTheme, Button
} from '@mui/material';
import {
  Map as Icon, MyLocation as LocationIcon,
  DirectionsBike, LocalShipping, PersonPinCircle, Refresh as RefreshIcon
} from '@mui/icons-material';
import { socket } from '../App';

const ESTADO_COLORS = {
  asignado: 'info',
  recogido: 'warning',
  en_camino: 'success',
};

export default function TrackingMap() {
  const theme = useTheme();
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivos = async () => {
    try {
      const res = await fetch('/api/tracking/activos', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.success) setActivos(data.data || []);
    } catch (e) {
      console.error('Error fetching tracking:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivos();
    const interval = setInterval(fetchActivos, 10000);
    socket.on('location-update', (data) => {
      setActivos(prev => prev.map(p =>
        p.mensajero?.id === data.mensajero_id
          ? { ...p, mensajero: { ...p.mensajero, latitud: data.latitud, longitud: data.longitud } }
          : p
      ));
    });
    return () => { clearInterval(interval); socket.off('location-update'); };
  }, []);

  const openMap = (lat, lng) => {
    window.open(`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`, '_blank');
  };

  const openDirections = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Tracking en Tiempo Real</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchActivos} sx={{ ml: 'auto' }}>
          Actualizar
        </Button>
        <Chip label={`${activos.length} activos`} color="primary" size="small" />
      </Box>

      {activos.length === 0 && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <PersonPinCircle sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
          <Typography variant="h6" color="textSecondary">No hay mensajeros en servicio activo</Typography>
          <Typography variant="body2" color="textSecondary">Los mensajeros aparecerán aquí cuando tengan pedidos asignados</Typography>
        </Paper>
      )}

      {loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">Cargando tracking...</Typography>
        </Paper>
      )}

      {activos.length > 0 && (
        <Grid container spacing={3}>
          {/* Mapa embebido */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: 600, overflow: 'hidden', position: 'relative' }}>
              <Typography variant="subtitle2" gutterBottom>Mapa de Mensajeros</Typography>
              <Box
                component="iframe"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=-80,-5,-60,15&layer=mapnik&marker=${activos[0]?.mensajero?.latitud || 4.711},${activos[0]?.mensajero?.longitud || -74.072}`}
                sx={{ width: '100%', height: '92%', border: 'none', borderRadius: 1 }}
                title="Mapa de tracking"
              />
            </Paper>
          </Grid>

          {/* Lista de mensajeros activos */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 600, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>Mensajeros en Ruta</Typography>
              <List dense>
                {activos.map((pedido) => {
                  const m = pedido.mensajero;
                  if (!m) return null;
                  return (
                    <React.Fragment key={pedido.id}>
                      <ListItem alignItems="flex-start" sx={{ borderRadius: 2, mb: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            <DirectionsBike />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">{m.nombre}</Typography>
                              <Chip label={pedido.estado} size="small" color={ESTADO_COLORS[pedido.estado] || 'default'} />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" display="block">
                                📍 {parseFloat(m.latitud || 0).toFixed(4)}, {parseFloat(m.longitud || 0).toFixed(4)}
                              </Typography>
                              {m.vehiculo_tipo && (
                                <Typography variant="caption" display="block" color="textSecondary">
                                  🚗 {m.vehiculo_tipo} · {m.vehiculo_placa || 'sin placa'}
                                </Typography>
                              )}
                              {pedido.cliente && (
                                <Typography variant="caption" display="block" color="textSecondary">
                                  👤 Cliente: {pedido.cliente.nombre}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Button size="small" variant="outlined" onClick={() => openMap(m.latitud, m.longitud)}>
                                  Ver mapa
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => openDirections(m.latitud, m.longitud)}>
                                  Direcciones
                                </Button>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* Cards resumen */}
          {activos.map((pedido) => {
            const m = pedido.mensajero;
            if (!m) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={`card-${pedido.id}`}>
                <Card>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: 14 }}>
                        {m.nombre?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">{m.nombre}</Typography>
                        <Typography variant="caption" color="textSecondary">{m.vehiculo_tipo || 'Mensajero'}</Typography>
                      </Box>
                      <Chip label={pedido.estado} size="small" color={ESTADO_COLORS[pedido.estado] || 'default'} sx={{ ml: 'auto' }} />
                    </Box>
                    <Typography variant="caption" display="block">
                      🎯 Pedido #{pedido.id}
                    </Typography>
                    <Typography variant="caption" display="block" noWrap>
                      📍 {pedido.origen_direccion?.substring(0, 40) || 'N/A'}
                    </Typography>
                    <Typography variant="caption" display="block" noWrap>
                      🏁 {pedido.destino_direccion?.substring(0, 40) || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
